import React from "react";
import VehicleDetailSection, { SectionType } from "@/pages/fleet/reports/components/VehicleDetailSection";
import type { VehicleDetailData, VehicleSummary } from "@/types/fleet-reports.v2";

type Period = { from: Date; to: Date };

type ReportType = 'vehicles' | 'maintenance' | 'fuel';

const REPORT_CONFIG: Record<ReportType, {
  title: string;
  description: string;
  icon: string;
  visibleSections: SectionType[];
  showKPIs: boolean;
}> = {
  vehicles: {
    title: "Detalhamento por Veículo",
    description: "Visão completa de cada veículo: combustível, manutenções, pneus, despesas, documentos e viagens.",
    icon: "🚗",
    visibleSections: ['summary', 'fuel', 'maintenance', 'tires', 'expenses', 'documents', 'trips'],
    showKPIs: true,
  },
  maintenance: {
    title: "Relatório de Manutenções",
    description: "Planos de manutenção e ordens de serviço por veículo.",
    icon: "🔧",
    visibleSections: ['maintenance'],
    showKPIs: false,
  },
  fuel: {
    title: "Relatório de Abastecimentos",
    description: "Histórico de abastecimentos por veículo.",
    icon: "⛽",
    visibleSections: ['fuel'],
    showKPIs: false,
  },
};

type Props = {
  reportType: ReportType;
  vehiclesSummary: VehicleSummary[];
  allVehiclesDetails: Map<string, VehicleDetailData>;
  isLoadingDetails?: boolean;
  period: Period;
};

export default function SpecializedReportWrapper({
  reportType,
  vehiclesSummary,
  allVehiclesDetails,
  isLoadingDetails,
  period,
}: Props) {
  const config = REPORT_CONFIG[reportType];
  
  if (!config) {
    return (
      <div className="bg-card border border-border rounded-xl p-8 text-center">
        <p className="text-muted-foreground">
          Tipo de relatório não suportado: {reportType}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header do Relatório */}
      <div className="bg-card border border-border rounded-xl p-5 shadow-sm">
        <div className="space-y-1">
          <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
            <span>{config.icon}</span>
            {config.title}
          </h2>
          <p className="text-sm text-muted-foreground">
            {vehiclesSummary.length} veículo(s) ordenado(s) por custo total (maior → menor).
            {' '}{config.description}
          </p>
        </div>
      </div>

      {/* Lista de Veículos */}
      {vehiclesSummary.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-8 text-center">
          <p className="text-muted-foreground">
            Nenhum veículo encontrado no período selecionado.
          </p>
        </div>
      ) : (
        vehiclesSummary.map((v, index) => (
          <VehicleDetailSection
            key={v.vehicle_id}
            rank={index + 1}
            vehicle={allVehiclesDetails.get(v.vehicle_id) ?? null}
            summaryFallback={v}
            period={period}
            isLoading={isLoadingDetails}
            visibleSections={config.visibleSections}
            showKPIs={config.showKPIs}
          />
        ))
      )}
    </div>
  );
}
